import { useMemo, memo, useCallback } from 'react';
import { useAtomValue } from 'jotai';

import { JobPosition, Restaurant } from '@restworld/utility-types';
import { JobPositionCard, JobPositionCardProps } from '../../legacy';
import { WorkerJpCard, WorkerVenueCard } from '../../legacy';
import { useAuth } from '@restworld/feature-authentication';
import { cn, formatJobPositionSalary, salaryLabelsFromT, useAnalytics } from '@restworld/utils-common';
import { useTranslation } from 'next-i18next';

import { PaginationButton } from './show-more-button';
import { explorerRestaurantListAtom, isRestaurantListAvailableAtom } from '../job-explorer-map';
import DataNotAvailableUI from './data-not-available-ui';
import { isRestaurantsExplorerAtom } from '../job-explorer-map';
import {
  activeJpIdAtom,
  jobLocationPositionListAtom,
  jobPositionTotalCountAtom,
  hasMapViewAtom,
  jobListParamsAtom,
} from '../job-explorer-list.atoms';
import { checkIfJobPositionIsOutsideItaly } from '../../../utils';
import { useJobLocationPositionList, useRQueryFetchExplorerRestaurantList } from '../job-explorer.hooks';
import { jobExplorerPageContentAtom } from '../job-explorer-filters.atoms';

// We combine job positions and restaurants into a single list with a 2:1 ratio.
// For example, we load 7 job positions and 3 restaurants initially, the combined list
// will alternate in the sequence: two job positions, one restaurant, and so on.
// This pattern continues as more data is loaded. Once one of the lists is exhausted,
// the remaining items from the other list are appended to the end.

export const JobExplorerSidebar = ({ ambassadorCode, testId }: { ambassadorCode?: string; testId?: string }) => {
  const hasMapView = useAtomValue(hasMapViewAtom);
  const jobPositionTotalCount = useAtomValue(jobPositionTotalCountAtom);
  const jobLocationPositionList = useAtomValue(jobLocationPositionListAtom);

  const activeJpId = useAtomValue(activeJpIdAtom);
  const isRestaurantListAvailable = useAtomValue(isRestaurantListAvailableAtom);
  const isRestaurantExplorer = useAtomValue(isRestaurantsExplorerAtom);
  const explorerRestaurantList = useAtomValue(explorerRestaurantListAtom);
  const jobExplorerPageContent = useAtomValue(jobExplorerPageContentAtom);
  const explorerRestaurantCount = explorerRestaurantList?.length || 0;

  const isJobPositionListAvailable = useMemo(() => jobLocationPositionList?.length > 0, [jobLocationPositionList]);

  useJobLocationPositionList();
  useRQueryFetchExplorerRestaurantList();

  const combinedRestaurantAndJobPositionList = useMemo(() => {
    const result: (JobPosition | Restaurant)[] = [];

    let i = 0;
    let j = 0;

    while (i < jobLocationPositionList?.length || j < explorerRestaurantList?.length) {
      // Add up to 2 JobPositions
      for (let count = 0; count < 2 && i < jobLocationPositionList?.length; count++) {
        result.push(jobLocationPositionList[i++]);
      }

      // Add 1 Restaurant
      if (j < explorerRestaurantList.length) {
        result.push(explorerRestaurantList[j++]);
      }
    }

    return result;
  }, [explorerRestaurantList, jobLocationPositionList]);

  const restaurantExplorerSidebarList = useMemo(
    () =>
      isRestaurantListAvailable ? (
        <>
          <JobExplorerListWrapper
            hasMapView={hasMapView}
            isListEmpty={!isRestaurantListAvailable}
            isRestaurantExplorer={isRestaurantExplorer}
            data-cy="restaurant-explorer-list"
          >
            {[...explorerRestaurantList].map(
              ({ id, restaurant_name, restaurant_logo, address, slug, inserted_at, show_details }) => (
                <li className="w-full cursor-pointer md:max-w-sm" key={id} id={id}>
                  <JobPositionCard
                    {...{
                      restaurantName: restaurant_name,
                      city: address,
                      restaurantLogo: restaurant_logo,
                      redirectUrl: `/locale/${slug}`,
                      publishedAt: inserted_at,
                      isActive: activeJpId === id,
                      ambassadorCode,
                      cardVariant: 'restaurant',
                      showRestaurantDetails: show_details || show_details === true ? true : false,
                      restaurantAddress: address,
                    }}
                  />
                </li>
              )
            )}
          </JobExplorerListWrapper>
          <div className="flex mx-auto px-1 items-center max-w-lg pb-2">
            <PaginationButton />
          </div>
        </>
      ) : (
        <DataNotAvailableUI />
      ),
    [activeJpId, ambassadorCode, explorerRestaurantList, hasMapView, isRestaurantExplorer, isRestaurantListAvailable]
  );

  const jobPositionAndRestaurantExplorerSidebarList = useMemo(() => {
    if (jobPositionTotalCount === 0 && !explorerRestaurantCount) return <DataNotAvailableUI />;
    return (
      <>
        <div className="relative">
          <JobExplorerListWrapper
            hasMapView={hasMapView}
            isListEmpty={!isJobPositionListAvailable}
            isRestaurantExplorer={isRestaurantExplorer}
            data-cy="job-position-explorer-list"
          >
            {combinedRestaurantAndJobPositionList
              ?.filter((restaurantOrJobPosition) => {
                if (jobExplorerPageContent === 'jps-and-venues' || 'job_title' in restaurantOrJobPosition) return true;
                else return false;
              })
              .map((restaurantOrJobPosition) => {
                if ('job_title' in restaurantOrJobPosition) {
                  const jobPosition: JobPosition = restaurantOrJobPosition;
                  return (
                    <StyledListItem key={jobPosition?.id} id={jobPosition?.id} hasMapView={hasMapView}>
                      <ExplorerCardView
                        {...{
                          data: jobPosition,
                          isActive: activeJpId === jobPosition?.id,
                          ambassadorCode,
                        }}
                      />
                    </StyledListItem>
                  );
                } else {
                  const restaurant: Restaurant = restaurantOrJobPosition;
                  return (
                    <StyledListItem key={restaurant?.id} id={restaurant?.id} hasMapView={hasMapView}>
                      <WorkerVenueCard
                        venueLogo={restaurant.restaurant_logo}
                        city={restaurant.address}
                        firstSubDate={restaurant.inserted_at}
                        href={`/locale/${restaurant.slug}`}
                        venueName={restaurant.restaurant_name}
                        wrapperStyle="w-full h-full"
                      />
                    </StyledListItem>
                  );
                }
              })}
            {hasMapView && (
              <div className="flex items-center pb-2 bg-neutral-10 w-full">
                <PaginationButton fullwidth />
              </div>
            )}
          </JobExplorerListWrapper>
          {!hasMapView && (
            <div className="w-full flex justify-center pt-4 lg:pb-10">
              <PaginationButton fullwidth={false} />
            </div>
          )}
        </div>
      </>
    );
  }, [
    activeJpId,
    ambassadorCode,
    combinedRestaurantAndJobPositionList,
    explorerRestaurantCount,
    hasMapView,
    isJobPositionListAvailable,
    isRestaurantExplorer,
    jobExplorerPageContent,
    jobPositionTotalCount,
  ]);
  const explorerList = useMemo(() => {
    if (isRestaurantExplorer) return restaurantExplorerSidebarList;
    return jobPositionAndRestaurantExplorerSidebarList;
  }, [isRestaurantExplorer, restaurantExplorerSidebarList, jobPositionAndRestaurantExplorerSidebarList]);

  return <div data-cy={testId}>{explorerList}</div>;
};
export default JobExplorerSidebar;
export const ExplorerCardView = memo(({ ambassadorCode, ...rest }: JobPositionCardType) => {
  const { jobPositionProps } = useJobPositionCard({ ...rest });
  const auth = useAuth();
  const { trackEvent } = useAnalytics();
  const sortFilterParams = useAtomValue(jobListParamsAtom);

  const isJobPositionOutsideItaly = checkIfJobPositionIsOutsideItaly({
    address: jobPositionProps?.address,
    country: jobPositionProps?.country,
  });
  const recordViewJobPositionEvent = useCallback(
    (jobProps: JobPositionCardProps) => {
      trackEvent('view_job', {
        email: auth?.user?.email,
        role: auth?.user?.type,
        origin: 'job_explorer_list_view',
        id: jobProps?.id,
        job_title: jobProps?.jobTitle,
        occupation: jobProps?.occupation,
        salary_min: jobProps?.salaryMin,
        salary_max: jobProps?.salaryMax,
        job_type: jobProps?.jobType,
        published_at: jobProps?.publishedAt,
        restaurant_name: jobProps?.restaurantName,
        city: jobProps?.city,
        searchParams: sortFilterParams,
      });
    },
    [auth?.user?.email, auth?.user?.type, sortFilterParams, trackEvent]
  );
  return (
    <WorkerJpCard
      {...{ ...jobPositionProps, isJobPositionOutsideItaly }}
      onClick={() => recordViewJobPositionEvent(jobPositionProps)}
      wrapperStyle="w-full h-full"
    />
  );
});
const useJobPositionCard = ({ data, isActive }: JobPositionCardType) => {
  const {
    published_at,
    restaurant: {
      locality = '',
      restaurant_name: restaurantName = '',
      restaurant_logo = '',
      country = '',
      address = '',
      address_lat = 0,
      address_lon = 0,
    } = {},
    job_title,
    max_salary,
    min_salary,
    salary,
    job_type_var,
    occupation,
    semantic_unique_name: jobPositionId,
    id,
    already_applied,
    salary_currency,
  } = data || {};
  const { t } = useTranslation('common');
  const salaryLabel = useMemo(() => (data ? formatJobPositionSalary(data, salaryLabelsFromT(t)) ?? '' : ''), [data, t]);
  const jobPositionProps = useMemo(
    () => ({
      publishedAt: published_at,
      city: locality,
      jobTitle: job_title,
      jobType: job_type_var?.label,
      occupation: occupation?.label_it?.label,
      salaryMin: min_salary,
      salaryMax: max_salary,
      salary: salary?.toString(),
      restaurantName,
      showRestaurantDetails: data?.show_restaurant_details,
      restaurantLogo: restaurant_logo,
      redirectUrl: jobPositionId ? `/posizione/${jobPositionId}` : '',
      isActive,
      hasAlreadyApplied: already_applied,
      country,
      address,
      address_lat,
      address_lon,
      salaryCurrency: salary_currency,
      salaryLabel,
    }),
    [
      salaryLabel,
      address,
      already_applied,
      country,
      data?.show_restaurant_details,
      id,
      isActive,
      jobPositionId,
      job_title,
      job_type_var?.label,
      locality,
      max_salary,
      min_salary,
      occupation?.label_it?.label,
      published_at,
      restaurantName,
      restaurant_logo,
      salary,
      address_lon,
      address_lat,
      salary_currency,
    ]
  );
  return { jobPositionProps };
};
const JobExplorerListWrapper = ({
  hasMapView,
  isListEmpty,
  isRestaurantExplorer,
  className,
  ...props
}: {
  hasMapView?: boolean;
  isListEmpty?: boolean;
  isRestaurantExplorer?: boolean;
} & React.HTMLAttributes<HTMLUListElement>) => (
  <ul
    className={cn(
      'max-w-screen-2xl mx-auto scrollbar-none lg:h-[calc(100vh-249px)] overflow-y-auto overflow-x-hidden',
      hasMapView ? 'pr-2' : 'pr-0',
      !hasMapView && 'h-full lg:h-[calc(100vh-22.4rem)]',
      hasMapView
        ? 'flex flex-col gap-4 h-full items-center py-2 md:flex-row md:flex-wrap md:mx-auto md:justify-center md:items-start lg:flex-col lg:flex-nowrap lg:justify-start lg:pb-24'
        : 'grid grid-cols-1 gap-[10px] place-items-center md:grid-cols-2 lg:grid-cols-3 lg:px-4 xl:grid xl:grid-cols-4 2xl:px-0',
      isListEmpty && 'md:h-full',
      '[&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-thumb]:bg-gray-200 [&::-webkit-scrollbar-thumb]:rounded [scrollbar-width:thin] [scrollbar-color:theme(colors.gray.200)_transparent]',
      className
    )}
    {...props}
  />
);
const StyledListItem = ({
  hasMapView,
  className,
  ...props
}: { hasMapView?: boolean } & React.HTMLAttributes<HTMLLIElement>) => (
  <li
    className={cn(
      'cursor-pointer grid place-items-center w-full h-full',
      hasMapView ? 'md:w-fit' : 'w-full',
      className
    )}
    {...props}
  />
);

type JobPositionCardType = {
  data: Partial<JobPosition>;
  isActive?: boolean;
  ambassadorCode?: string;
};
